import { supabase } from './supabase';

// Calculate speaker profile completion percentage
export const getSpeakerProfileCompletion = (speaker) => {
  const requiredFields = [
    { key: 'name', value: speaker?.name },
    { key: 'title', value: speaker?.title },
    { key: 'bio', value: speaker?.bio },
    { key: 'photo_url', value: speaker?.photo_url },
    { key: 'email', value: speaker?.email },
    { key: 'church_name', value: speaker?.church_name },
    { key: 'topics', value: speaker?.topics?.length > 0 },
  ];
  
  const completed = requiredFields.filter(f => f.value).length;
  const percentage = Math.round((completed / requiredFields.length) * 100);
  
  const missingFields = requiredFields
    .filter(f => !f.value)
    .map(f => f.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
  
  return { percentage, missingFields, completed, total: requiredFields.length };
};

// Calculate church admin profile completion percentage
export const getChurchProfileCompletion = (church) => {
  const requiredFields = [
    { key: 'name', value: church?.name },
    { key: 'pastor_name', value: church?.pastor_name },
    { key: 'city', value: church?.city },
    { key: 'state', value: church?.state },
    { key: 'phone', value: church?.phone },
    { key: 'email', value: church?.email },
    { key: 'website_url', value: church?.website_url },
  ];
  
  const completed = requiredFields.filter(f => f.value).length;
  const percentage = Math.round((completed / requiredFields.length) * 100);
  
  const missingFields = requiredFields
    .filter(f => !f.value)
    .map(f => f.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
  
  return { percentage, missingFields, completed, total: requiredFields.length };
};

// Calculate attendee profile completion percentage
export const getAttendeeProfileCompletion = (attendee) => {
  const requiredFields = [
    { key: 'full_name', value: attendee?.full_name },
    { key: 'photo_url', value: attendee?.photo_url },
    { key: 'church_name', value: attendee?.church_name },
    { key: 'city', value: attendee?.city },
    { key: 'state', value: attendee?.state },
    { key: 'phone', value: attendee?.phone },
    { key: 'interests', value: attendee?.interests?.length > 0 },
  ];
  
  const completed = requiredFields.filter(f => f.value).length;
  const percentage = Math.round((completed / requiredFields.length) * 100);
  
  const missingFields = requiredFields
    .filter(f => !f.value)
    .map(f => f.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
  
  return { percentage, missingFields, completed, total: requiredFields.length };
};

// Fetch speakers with incomplete profiles
export const getIncompleteSpeakerProfiles = async (threshold = 100) => {
  const { data: speakers, error } = await supabase
    .from('speakers')
    .select('*')
    .order('name');
  
  if (error) throw error;
  
  const incompleteProfiles = speakers
    .map(speaker => ({
      ...speaker,
      completion: getSpeakerProfileCompletion(speaker)
    }))
    .filter(s => s.completion.percentage < threshold)
    .sort((a, b) => a.completion.percentage - b.completion.percentage);
  
  return incompleteProfiles;
};

// Fetch churches with incomplete profiles
export const getIncompleteChurchProfiles = async (threshold = 100) => {
  const { data: churches, error } = await supabase
    .from('churches')
    .select('*')
    .order('name');
  
  if (error) throw error;
  
  const incompleteProfiles = churches
    .map(church => ({
      ...church,
      completion: getChurchProfileCompletion(church)
    }))
    .filter(c => c.completion.percentage < threshold)
    .sort((a, b) => a.completion.percentage - b.completion.percentage);
  
  return incompleteProfiles;
};

// Fetch attendees with incomplete profiles
export const getIncompleteAttendeeProfiles = async (threshold = 100) => {
  const { data: profiles, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('role', 'attendee')
    .order('full_name');
  
  if (error) throw error;
  
  const incompleteProfiles = profiles
    .map(attendee => ({
      ...attendee,
      name: attendee.full_name || attendee.email,
      completion: getAttendeeProfileCompletion(attendee)
    }))
    .filter(a => a.completion.percentage < threshold)
    .sort((a, b) => a.completion.percentage - b.completion.percentage);
  
  return incompleteProfiles;
};

// Log a reminder sent
export const logReminderSent = async (recipientType, recipientId, recipientEmail, reminderType = 'profile_completion') => {
  const { data, error } = await supabase
    .from('profile_reminders')
    .insert({
      recipient_type: recipientType,
      recipient_id: recipientId,
      recipient_email: recipientEmail,
      reminder_type: reminderType,
      sent_at: new Date().toISOString()
    });
  
  return { data, error };
};

// Get reminder history for a recipient
export const getReminderHistory = async (recipientType, recipientId) => {
  const { data, error } = await supabase
    .from('profile_reminders')
    .select('*')
    .eq('recipient_type', recipientType)
    .eq('recipient_id', recipientId)
    .order('sent_at', { ascending: false });
  
  return { data, error };
};

// Check if reminder was sent recently (within days)
export const wasReminderSentRecently = async (recipientType, recipientId, days = 7) => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  
  const { data, error } = await supabase
    .from('profile_reminders')
    .select('id')
    .eq('recipient_type', recipientType)
    .eq('recipient_id', recipientId)
    .gte('sent_at', cutoffDate.toISOString())
    .limit(1);
  
  if (error) return false;
  return data && data.length > 0;
};

// Generate reminder email content for attendee
export const generateAttendeeReminderEmail = (attendee, completion) => {
  const missingList = completion.missingFields.map(f => `• ${f}`).join('\n');
  
  return {
    subject: `Complete Your Profile - ${completion.percentage}% Done`,
    body: `
Hello ${attendee.full_name || 'Attendee'},

Your profile on ConfPlanners is ${completion.percentage}% complete.

To get the most out of your conference experience, please complete the following:

${missingList}

Complete your profile here: ${window.location.origin}/my-profile

A complete profile helps you:
✓ Get personalized event recommendations
✓ Receive relevant event reminders
✓ Connect with other attendees and speakers

Thank you for being part of our community!

The ConfPlanners Team
    `.trim()
  };
};

// Generate reminder email content for speaker
export const generateSpeakerReminderEmail = (speaker, completion) => {
  const missingList = completion.missingFields.map(f => `• ${f}`).join('\n');
  
  return {
    subject: `Complete Your Speaker Profile - ${completion.percentage}% Done`,
    body: `
Hello ${speaker.name || 'Speaker'},

Your speaker profile on ConfPlanners is ${completion.percentage}% complete. 

To help event organizers find and connect with you, please complete the following:

${missingList}

Complete your profile here: ${window.location.origin}/speaker/profile

A complete profile helps you:
✓ Get discovered by event organizers
✓ Showcase your ministry and expertise
✓ Receive more speaking invitations

Thank you for being part of our community!

The ConfPlanners Team
    `.trim()
  };
};

// Generate reminder email content for church admin
export const generateChurchReminderEmail = (church, completion) => {
  const missingList = completion.missingFields.map(f => `• ${f}`).join('\n');
  
  return {
    subject: `Complete Your Church Profile - ${completion.percentage}% Done`,
    body: `
Hello ${church.pastor_name || 'Church Administrator'},

Your church profile for "${church.name || 'Your Church'}" on ConfPlanners is ${completion.percentage}% complete.

To help attendees find your events and connect with your ministry, please complete the following:

${missingList}

Complete your profile here: ${window.location.origin}/church/settings

A complete profile helps you:
✓ Build trust with potential attendees
✓ Improve event visibility
✓ Connect with speakers and other churches

Thank you for being part of our community!

The ConfPlanners Team
    `.trim()
  };
};
